import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, history } from 'umi';
import CommonFilter from '@intelligence/components/CommonFilter';
import TableModule from '@intelligence/pages/Compare/PcConsoleGamesBeta/components/TableModule';
import {
  DEFAULTFILTERVALUE as defaultFilterValue,
  FILTERKEYLIST as filterKeyList,
  DEFAULTINPUTLIST as defaultInputList,
} from './const';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { useDeepCompareEffect } from 'ahooks';
import useSameStartDate from '@intelligence/pages/Compare/PcConsoleGamesBeta/hooks/useSameStartDate';
import useInitFilterHook from '@intelligence/hooks/useInitFilterHook';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import NoEntityCard from '@/pages/intelligence/pages/Compare/components/NoEntityCard';
import AllGameSelector from '@/pages/intelligence/components/AllGameSelector';
import { followSearchPullDown } from '@/pages/intelligence/services/following';
import type { SelectGames } from '@/pages/intelligence/components/AllGameSelector/types';
import { updateFilterSaveList, getGameInfoById } from '@/pages/intelligence/services/common';
import { sortTableByMetric } from '@intelligence/utils/tableUtils';
import VisualList from '@intelligence/pages/Compare/PcConsoleGamesBeta/components/VisualList';
import {
  getUserSaveParams,
  USERSAVEKEY_COMPARE_PCCONSOLE as userSaveFilterValueKey,
  handleComparePCConsoleFilterParamsToApi as handleFilterToApiParams,
} from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import MscienceIcon from '@intelligence/assets/svg/dataSource/mscience-bg.svg';
import { getGamesColorMap } from '@intelligence/pages/Compare/PcConsoleGamesBeta/utils';
import useMarketFilterApi, { MarketSpecailType } from '@/pages/intelligence/hooks/useMarketSelector';
import { getURLSearchParams, updateURLSearchParams } from '@intelligence/utils/commonUtils';
import useConfigureTexts from '@intelligence/hooks/useConfigureTexts';
import { handleMetricExplanationTipsClick } from '@intelligence/v3/utils/topcharts';

const tableSetMax = (tableData: any[], currentMetric: string) => {
  const maxMetricObject: any = {};
  const newList: any = [];
  tableData?.forEach((item: any) => {
    const mvl = item?.metric_value_list || [];
    mvl?.forEach((subItem: any) => {
      const key = subItem?.metric;
      if (!maxMetricObject[key]) {
        maxMetricObject[key] = subItem?.value;
      } else {
        maxMetricObject[key] = Math.max(subItem?.value, maxMetricObject[key]);
      }
    });
  });
  tableData?.forEach((item: any, idx: number) => {
    const mvl = item?.metric_value_list || [];
    const metricValueList = mvl.map((subItem: any) => {
      const key = subItem?.metric;
      const maxValue = maxMetricObject[key];
      const val = subItem?.value / maxValue || 0;
      const domWidth = val * 100;
      return {
        ...subItem,
        domWidth,
      };
    });
    newList.push({ ...item, metric_value_list: metricValueList, idx });
  });
  const sortTable = sortTableByMetric({
    tableData: newList,
    isRank: false,
    sortParams: {
      sortVal: 'desc',
      sortMetric: currentMetric,
      ratioType: 0,
    },
    metricProperty: 'metric',
    growthProperty: 'growth_ratio',
    listProperty: 'metric_value_list',
  });
  return sortTable.slice(0);
};

// SameStartDate页面
const SameStartDate = (props: any) => {
  const { entityType = 'combine' } = props;
  const isCompareGameSelection = true;
  const { formatMessage } = useIntlExtend();
  const { configureTexts } = useConfigureTexts();
  /** 筛选器value数据 **/
  const [filterValue, setFilterValue] = useState<any>(defaultFilterValue);
  // 控制游戏筛选器弹窗是否显示
  const [allGamesvisible, setAllGamesvisible] = useState(false);
  // 固选的已选游戏
  // const [fixedSelectionGameEntities, setFixedSelectionGameEntities] = useState<SelectGames[]>([]);
  // 过滤掉固选的已选游戏
  // const [checkedListCur, setCheckedListCur] = useState<SelectGames[]>(checkedList);
  // const [isGameEmpty, setIsGameEmpty] = useState(false);
  const [checkedGamesList, setCheckedGamesList] = useState<SelectGames[]>([]);
  // 供外部接口使用
  const [paramsRegionOption, setParamsRegionOption] = useState<any[]>([]);
  // 游戏对应颜色映射
  const [gamesColorMap, setGamesColorMap] = useState<{
    [key: string]: string;
  }>();

  // 用户是否手动修改了国家区域
  const userManuallyChangedRegionRef = useRef(Boolean(getURLSearchParams('custom_region')));

  const userSegmentChangedRegionRef = useRef(Boolean(getURLSearchParams('cascader')));

  const userSegmentCloseRef = useRef(false);

  // URL games parameter handling
  const location = useLocation();
  const urlGamesProcessedRef = useRef(false);
  const pendingUrlGamesRef = useRef<SelectGames[] | null>(null);
  const updateFilterDatasRef = useRef<any>(null);

  const MSCIENCE_ICON = <img style={{ verticalAlign: 'text-top' }} src={MscienceIcon} />;

  const filterCfg = {
    filterKeyList,
    regionFilterParams: {},
    defaultValue: defaultFilterValue,
    resetFilterValue: {
      ...defaultFilterValue,
    },
    showConfirmButton: false,
    isCustomLayoutFilter: true,
    specialParams: {
      // regionOptions, // 国家筛选器options,
      defalutMarket: ['global'], // 没取到交集用的默认值
      disableNoChina: true,
      digitalSegementTips: formatMessage({ id: 'dbi_info_segement_mscience' }, { mscienceIcon: MSCIENCE_ICON }),
      entityType,
      showSelectType: false,
      rangeTimeOtherStyle: true,
      isCompareGameSelection,
      userSaveFilterValueKey,
      cascaderFooterMsg: configureTexts?.segment_for_sales_select_tips,
    },
  };

  // SameStartDate接口调用hooks
  const {
    fetchParams,
    fetchLoading,
    metricValueGameList,
    getIndexPageData,
    currentMetric,
    visualisLoading,
    metricListGameLineData,
    responseQueryDate,
  } = useSameStartDate({
    entityType,
    filterCfg,
    filterValue,
    setFilterValue,
  });

  // 用户需要保存的操作记录
  const updateUserSaveParams = async (typeValu: string, params: any) => {
    // console.log('handleFilterChange-paramsparams', params);
    await updateFilterSaveList({
      name: typeValu,
      type: typeValu,
      data: JSON.stringify(params || {}),
    });
  };

  /**
   * 筛选器统一值处理后，confirm回调
   */
  const fetchFilterChange = (payload: any) => {
    // console.log('handleFilterChange-payload', payload);
    // 若有需要保存用户操作记录，则调用
    updateUserSaveParams(userSaveFilterValueKey, getUserSaveParams(payload, userSaveFilterValueKey));
    // 更新游戏选择list
    setCheckedGamesList(payload?.allGames?.checkedList);
    const params = handleFilterToApiParams(fetchParams, {
      ...payload,
      cascader: dealSegmentAllData(payload.cascader),
      cascadersByDownload: payload.cascader,
    });
    getIndexPageData(params, payload);
  };
  const dealSegmentAllData = (data: any) => {
    const { value } = JSON.parse(JSON.stringify(data));
    const targetData: any = defaultInputList.find((item: any) => item.key === 'cascader');
    Object.keys(value).forEach((key) => {
      targetData.options.children.map((targetItem: any) => {
        if (targetItem.value === key && value[key].length === targetItem.children.length) {
          value[key] = ['All'];
        }
      });
    });
    return {
      value,
    };
  };
  const onAddGamesHandle = () => {
    setAllGamesvisible(true);
  };

  // filter公共hooks，暴露全局统一处理filterValue方法，再调接口
  const { updateFilterDatas } = useInitFilterHook({
    entityType,
    filterCfg,
    filterValue,
    setFilterValue,
    filterConfirm: fetchFilterChange,
    isBasicLoading: false,
    ifGetFilterExtend: false,
    ifSetFilterExtend: true,
    shouldSetUSOrGlobalRegionByMetrics: true,
    pageId: 'segmentDefauleValue',
  });

  // Store updateFilterDatas in ref for URL games processing
  useEffect(() => {
    updateFilterDatasRef.current = updateFilterDatas;
    // Process pending URL games if available
    if (pendingUrlGamesRef.current && updateFilterDatas) {
      const gamesList = pendingUrlGamesRef.current;
      const gameIdsValues = gamesList.map((g) => g.id);
      const payloadValue = {
        ...defaultFilterValue,
        allGames: {
          ...defaultFilterValue.allGames,
          checkedList: gamesList,
          value: gameIdsValues,
        },
      };
      updateFilterDatas(payloadValue);
      pendingUrlGamesRef.current = null;
    }
  }, [updateFilterDatas]);

  const restoreDefault = useRef(false);

  // 设置特殊结点， 只判断处理Cascader关闭处理 platform_cascader 关闭后直接调用
  const specialCascader = useRef(false);
  const handleChangeExtra = (key: string, value: any, newValue: any) => {
    if (key === 'region') {
      userManuallyChangedRegionRef.current = true;
      updateURLSearchParams({ custom_region: true });
    }
    userSegmentCloseRef.current = true;
    specialCascader.current = false;
    if (key === 'cascader') {
      const mapNullArr = Object.values(value.value);
      restoreDefault.current = true;
      mapNullArr.forEach((item: any) => {
        if (item.length) {
          restoreDefault.current = false;
        }
      });
      userSegmentChangedRegionRef.current = true;
      userSegmentCloseRef.current = newValue.isClose || false;
    }

    if (key === 'platform_cascader') {
      userSegmentCloseRef.current = newValue.isClose || false;
      specialCascader.current = true;
    }
  };

  /**
   * 游戏筛选器提交，confirm回调
   */
  const handleGameConfirm = (value: string[], checkedList: SelectGames[]) => {
    setAllGamesvisible(false);
    const payloadValue = {
      ...defaultFilterValue,
      allGames: {
        ...defaultFilterValue.allGames,
        checkedList,
        value,
      },
    };
    updateFilterDatas(payloadValue);
  };

  // 游戏筛选器关闭事件回调
  const handleClose = () => {
    setAllGamesvisible(false);
  };

  const uTable = useMemo(() => {
    const d = tableSetMax(metricValueGameList, currentMetric);
    return [...d];
  }, [metricValueGameList, currentMetric]);

  useEffect(() => {
    const colorsMap = getGamesColorMap(uTable);
    setGamesColorMap(colorsMap);
  }, [uTable]);

  // 删除游戏
  const deleteGameHandle = (gameId: string) => {
    const gameListTemp = checkedGamesList?.filter((item: any) => item.id !== gameId);
    setCheckedGamesList(gameListTemp);
    const gameIdsListTemp = gameListTemp?.map((item: any) => item.id);
    const filterValueTemp = {
      ...filterValue,
      allGames: {
        ...filterValue?.allGames,
        checkedList: gameListTemp,
        value: gameIdsListTemp,
      },
    };
    setFilterValue(filterValueTemp);
    updateFilterDatas(filterValueTemp);
    // 若有需要保存用户操作记录，则调用
    updateUserSaveParams(userSaveFilterValueKey, getUserSaveParams(filterValueTemp, userSaveFilterValueKey));
  };

  // 是否是使用ReleaseTime
  const isReleaseTime = useMemo(() => {
    return filterValue.align_to_release?.value;
  }, [filterValue]);

  // 是否在对比页没有选择游戏
  const isNoCompareGames = useMemo(() => checkedGamesList?.length === 0, [checkedGamesList]);

  const { regionOptions } = useMarketFilterApi({
    filterType: MarketSpecailType.metricTable,
  });

  // const updateRegionOptions = (options: any) => {
  //   console.log('updateRegionOptions-options', options);
  //   setParamsRegionOption(options);
  // };

  useDeepCompareEffect(() => {
    setParamsRegionOption(regionOptions);
  }, [regionOptions]);

  /** 页面打点 **/
  useEffect(() => {
    // @ts-ignore
    const pageLogConfig = dbiPageLogMes.comparePCConsoleGamesBeta;
    if (pageLogConfig) {
      buriedPointPage(pageLogConfig);
    }
  }, []);

  /** URL games parameter handling - auto-add games from ?games=id1,id2,... */
  useEffect(() => {
    // Only process once per page load
    if (urlGamesProcessedRef.current) return;

    const searchParams = new URLSearchParams(location.search);
    const gamesParam = searchParams.get('games');

    if (!gamesParam) return;

    const gameIds = gamesParam
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    if (gameIds.length === 0) return;

    // Mark as processed
    urlGamesProcessedRef.current = true;

    // Helper to clear games param from URL
    const clearGamesFromUrl = (): void => {
      const params = new URLSearchParams(location.search);
      params.delete('games');
      const newSearch = params.toString();
      history.replace(location.pathname + (newSearch ? `?${newSearch}` : ''));
    };

    // Fetch game info and populate the checkedGamesList
    const fetchGamesFromUrl = async (): Promise<void> => {
      try {
        const res = await getGameInfoById({
          entity_type: 'combine',
          ids: gameIds,
          level: 'custom',
          custom_fields: [
            'id',
            'entity_type',
            'cover',
            'entity_name',
            'publisher',
            'release_time',
          ],
        });

        if (res?.data) {
          const gamesData = res.data;
          const gamesList: SelectGames[] = gameIds
            .map((gameId) => {
              const gameInfo = gamesData[gameId];
              if (!gameInfo) return null;
              return {
                id: gameInfo.id || gameId,
                name: gameInfo.entity_name || '',
                cover: gameInfo.cover || '',
                entityType: gameInfo.entity_type || 'combine',
                publisher: gameInfo.publisher || '',
                releaseTime: gameInfo.release_time || '',
              } as SelectGames;
            })
            .filter(Boolean) as SelectGames[];

          if (gamesList.length > 0) {
            // Update the checked games list for UI display
            setCheckedGamesList(gamesList);

            // If updateFilterDatas is ready, call it immediately
            // Otherwise, store in pending ref to be processed later
            if (updateFilterDatasRef.current) {
              const gameIdsValues = gamesList.map((g) => g.id);
              const payloadValue = {
                ...defaultFilterValue,
                allGames: {
                  ...defaultFilterValue.allGames,
                  checkedList: gamesList,
                  value: gameIdsValues,
                },
              };
              updateFilterDatasRef.current(payloadValue);
            } else {
              // Store for later processing when updateFilterDatas becomes available
              pendingUrlGamesRef.current = gamesList;
            }
          }
        }
        // Clear URL after successful processing
        clearGamesFromUrl();
      } catch (error) {
        console.error('[SameStartDate] Failed to fetch games from URL:', error);
        // Still clear URL on error to prevent infinite retry
        clearGamesFromUrl();
      }
    };

    fetchGamesFromUrl();
  }, [location.search, location.pathname]);

  /** 页面打点 **/
  return (
    <div className={'dbi-pc-console-compare dbi-mobile-games-stats dbi-mobile-games-stats-kpi dbi-KPITrends-compage'}>
      {/* 筛选器 */}
      <CommonFilter
        timeFilterValues={{
          type: fetchParams.date_type,
          value: [fetchParams.start_date, fetchParams.end_date],
        }}
        filterConfirm={(...params: any) => {
          updateFilterDatas(
            ...params,
            userManuallyChangedRegionRef.current,
            {},
            userSegmentChangedRegionRef.current,
            userSegmentCloseRef.current,
            restoreDefault.current,
            specialCascader.current,
          );
        }}
        onChangeExtra={handleChangeExtra}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        filterCfg={filterCfg}
        defaultInputList={defaultInputList}
        isBasicLoading={false}
        regionOptions={regionOptions}
        // updateRegionOptions={updateRegionOptions}
      />
      {/* 加载动画 */}
      {fetchLoading ? (
        <>
          <Abnormal
            style={{
              margin: '0 0 20px 0',
              height: 678,
              backgroundColor: '#fff',
              borderRadius: 8,
            }}
            isLoading={true}
          />
          <Abnormal style={{ height: 200, backgroundColor: '#fff', borderRadius: 8 }} isLoading={true} />
        </>
      ) : (
        <>
          {/* 无数据 */}
          {isNoCompareGames ? (
            <NoEntityCard
              onAdd={onAddGamesHandle}
              title={formatMessage({ id: 'dbi_game_comparison_table' })}
              // isCompareGameSelection={isCompareGameSelection}
            />
          ) : (
            <>
              {/* 宽表 */}
              <TableModule
                isAlignToRelease={isReleaseTime}
                tableData={uTable}
                fetchParams={fetchParams}
                responseQueryDate={responseQueryDate}
                onChange={deleteGameHandle}
                currentMetric={currentMetric}
                paramsRegionOption={paramsRegionOption}
                loading={fetchLoading}
                entityType={entityType}
                gamesColorMap={gamesColorMap}
                setGamesColorMap={setGamesColorMap}
                tableSetMax={tableSetMax}
                onMetricExplanationClick={(key, payload) => {
                  handleMetricExplanationTipsClick(key, payload, {
                    filterValue,
                    onClickSetRegionUSOnly: updateFilterDatas,
                  });
                }}
              />
              {/* 可视化 */}
              <VisualList
                loading={visualisLoading}
                fetchParams={fetchParams}
                gamesColorMap={gamesColorMap}
                isAlignToRelease={isReleaseTime}
                compareGameList={checkedGamesList}
                responseQueryDate={responseQueryDate}
                gamesMetricData={metricListGameLineData}
                gamesMetricDataAggregation={metricValueGameList}
              />
            </>
          )}
        </>
      )}
      {/* 游戏筛选器打开弹窗内容 */}
      <AllGameSelector
        entityKey="id"
        visible={allGamesvisible}
        title={formatMessage({ id: 'dbi_compare_add_game_to_compare' })}
        selectedGameList={checkedGamesList}
        pleaseSelectTips={formatMessage({ id: 'dbi_please_a_select_game' })}
        searchPlaceholder={formatMessage({ id: 'dbi_search_any_game' })}
        rightCellTitle={formatMessage({ id: 'dbi_games' })}
        keywordsFetcher={followSearchPullDown}
        onConfirm={handleGameConfirm}
        onClose={handleClose}
        tipText={`${formatMessage({
          id: 'dbi_Choose_up_to_games',
        })}20${formatMessage({
          id: 'dbi_Choose_up_to_games_end',
        })}`}
        notSelectTips={'dbi_compare_pcconsole_game_selection_empty'}
        params={{ entity_type: entityType, game_type: 'current_game' }}
        chooseUpNum={20}
        hasShortCut={true}
      />
    </div>
  );
};

export default SameStartDate;
